/* eslint-disable max-lines-per-function */

import {observer} from 'mobx-react-lite'
import React, {useCallback, useMemo} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {Util} from 'yougile-util'
import {UiSlot} from '@api/components'
// eslint-disable-next-line no-restricted-imports
import {PermissionsChecker} from '@client/permissions-checker'
import {SharedBoardsClient} from '@client/shared-boards-client'
import {Backend} from '@common/backend'
import {Tracking, TrackingEvent} from '@common/tracking'
import {ChatSubscribersChooser} from '@components/chat/chat-subscribers-chooser'
import {Attachments} from '@components/common/base/attachments/attachments'
import {IconButton} from '@components/common/base/buttons'
import {showDeleteConfirm} from '@components/common/base/notifications'
import {EmojiPicker} from '@components/common/emoji-picker/emoji-picker'
import {MessageBox} from '@components/message-box'
import {CitedMessage} from '@components/right-panel/chat/chat-input/cited-message'
import {useRightPanelStore} from '@components/right-panel/right-panel'
import {CustomCKEditor} from '@components/right-panel/task-description/ck-editor'
import {useRecordScreencast} from '@components/screencast'
import {ShareNameChanger} from '@components/sharing/share-name-changer/share-name-changer'
import {getChatIdWithKind, getChatKind} from '@containers/hooks'
import {cn} from '@containers/html-utils'
import {globals} from '@extensions/globals-base'
import {ChatInputKeydownData} from '../../../../api/model/chat'
import {useChatStore} from '../chat-store'
import {handleEditCanceling, handleReplyCanceling} from '../utils'
import {useChatInputStore} from './chat-input-store'
import {MentionButton} from './mention-button'
import {UnseenButton} from './unseen-button'
import {useChatInputCustomEditor} from './use-chat-input-custom-editor'
import {useDropFiles} from './use-drop-files'

const TwAttach = tw(IconButton)`
  absolute
  bottom-8
  -left-[28px]
`

const TwEditor = tw.div<{$dark: boolean}>`
  flex-initial
  overflow-hidden
  message-text
  text-primary
  plain-text-regular
  flex
  grow
  h-full

  ${p => cn(p.$dark && 'dark')}
`

const TwActionsContainer = tw.div`
  absolute
  right-16
  bottom-12
  flex
  gap-8
`

const TwSend = tw(IconButton)``

const TwToggleEditor = tw(IconButton)``

const TwEmojiPicker = tw(EmojiPicker)``

const TwScreencast = tw(IconButton)``

const TwVoting = tw(IconButton)``

const TwMessageInput = tw.div<{$hidden: boolean}>`
  flex-auto
  flex
  flex-col
  relative
  min-h-[42.2px]
  mt-16
  ml-40
  mr-16
  py-12
  pl-16
  pr-112
  shadow-border-1
  shadow-grey-l60
  rounded-16
  bg-grey-l100
  ${p => cn(p.$hidden && 'hidden')}
`

const TwChatInputContainer = tw.div`
  flex
  justify-start
`

const TwChatInput = tw.div<{$padBottom: boolean}>`
  chat-item-input
  flex-initial
  flex
  flex-col
  bg-grey-l80
  border-solid
  border-0
  border-t-1
  border-grey-l100
  relative
  order-3
  ${p => cn(p.$padBottom && 'pb-16')}
`

export const ChatInput = observer(({headerHeight}: {headerHeight: number}) => {
  const [state, store] = useChatStore()
  const [inputState, inputStore] = useChatInputStore()
  const [, rightPanelStore] = useRightPanelStore()

  useDropFiles(inputStore)
  const hub = state.hub
  useRecordScreencast(
    {
      onDoneUpload(url, hub) {
        // ChatUtils.openChat(hub)
        const kind = getChatIdWithKind().kind
        let msg = ''
        if (kind === 'task') {
          msg = Lang.msg.screencast.messageWhenUploaded(hub.title)
        } else {
          msg = Lang.msg.screencast.messageWhenUploaded2
        }

        MessageBox.show(msg, 'success')
        setTimeout(() => {
          inputStore.sendUploadedFile(url, hub)
        }, 500)
      },
      onUploadFailed(file) {
        // Добавляем файл в список прикрепленных файлов
        inputStore.onChooseFiles([file])
      }
    },
    hub
  )

  const {editorConfig, updateEditorRef, editorRef, sendMessage, onEmojiSelect} =
    useChatInputCustomEditor(store, inputStore, hub)

  const showGuestNameChange = !!SharedBoardsClient.getId()
  const showSubscriberChooser = !state.noTabs && !globals.sharedId
  const padBottom = !showGuestNameChange && !showSubscriberChooser

  const canSend =
    inputState.hasText ||
    inputState.chosenFiles.length ||
    inputState.messageFiles.length

  const cancelReply = useCallback(async () => {
    await handleReplyCanceling({
      editor: editorRef?.current,
      chatStore: store,
      chatInputStore: inputStore
    })
  }, [store])

  const cancelEdit = useCallback(async () => {
    await handleEditCanceling({
      editor: editorRef?.current,
      chatStore: store,
      chatInputStore: inputStore
    })
  }, [store, inputStore, editorRef])

  const deleteEdited = useCallback(async () => {
    if (
      store.state.messageToEdit &&
      (store.state.messageToEdit.from === globals.currentUserId ||
        store.isExtensionCommentsEditor) &&
      (await showDeleteConfirm(Lang.msg.messageDeleteConfirm))
    ) {
      store.state.messageToEdit.deleted = true
      store.state.hub?.chat.updateMessagesCountIfNotLazy()
      store.state.hub?.commit()
      store.stopEdit()
      inputStore.clearTyped(true)
      editorRef?.current?.setData('')
      void Tracking.send(TrackingEvent.chatDeleteMessage)
    }
  }, [store, inputStore, editorRef])

  const noKeyboardScroll = useCallback((e: React.KeyboardEvent) => {
    if (e.code === 'PageUp' || e.code === 'PageDown') {
      e.preventDefault()
    }
  }, [])

  const hasText = inputStore.state.messageHtml.length

  const showVoting =
    useMemo(() => {
      return !!state.hub && getChatKind() !== 'personal'
    }, [hub?.id]) &&
    !hasText &&
    !store.isExtensionComments &&
    Backend.features.voting

  const showScreencast = !Util.isDesktop() && !hasText

  const onEditorKeyDown = useCallback(
    (data: ChatInputKeydownData) => {
      inputStore.onKeyDown({
        altKey: data.altKey,
        ctrlKey: data.ctrlKey,
        keyCode: data.keyCode,
        keystroke: data.keystroke,
        metaKey: data.metaKey,
        shiftKey: data.shiftKey
      })
    },
    [sendMessage]
  )
  const canEditSubscribers = useMemo(() => {
    if (!showSubscriberChooser) {
      return 'no'
    }
    if (state.noTabs) {
      return 'yes'
    }
    return PermissionsChecker.editWhoToNotifyMode(state.hub)
  }, [state.noTabs, state.hub, showSubscriberChooser])

  return (
    <TwChatInput
      $padBottom={padBottom}
      style={{maxHeight: `calc(100vh - ${headerHeight}px)`}}
    >
      <MentionButton />
      <UnseenButton />

      {state.messageToReply && (
        <CitedMessage
          hub={state.hub}
          message={state.messageToReply}
          onClose={cancelReply}
        />
      )}
      {state.messageToEdit && (
        <CitedMessage
          hub={state.hub}
          message={state.messageToEdit}
          onClose={cancelEdit}
          onDelete={deleteEdited}
        />
      )}
      <TwMessageInput id="chat-item-editor" $hidden={!state.canSendMessages}>
        {inputState.allowSendFiles && (
          <TwAttach
            icon="IconPaperclip"
            color="grey"
            size="small"
            wrapperSize="big"
            onClick={inputStore.onUploadClick}
          />
        )}
        <TwChatInputContainer>
          <UiSlot name="chat-input-left" />
          <TwEditor
            $dark={globals.theme === 'dark'}
            onKeyDown={noKeyboardScroll}
          >
            {state.hub && (
              <CustomCKEditor
                key={state.hub?.id}
                ref={updateEditorRef}
                type={inputState.inputType}
                config={editorConfig}
                onChange={inputStore.onEditorContentUpdate}
                autofocus={false}
                kindOf="chat-input"
                onKeyDown={onEditorKeyDown}
              />
            )}
          </TwEditor>
        </TwChatInputContainer>

        <TwActionsContainer>
          <UiSlot name="chat-input-actions" />
          <TwToggleEditor
            icon="IconLetterCase"
            size="small"
            color="grey"
            onClick={inputStore.toggleInputType}
            forcedState={
              inputState.inputType === 'classic' ? 'active' : undefined
            }
          />

          <TwEmojiPicker
            iconColor="grey"
            onSelect={onEmojiSelect}
            closeOnSelect
          />

          {showVoting && (
            <TwVoting
              icon="IconCustomVoting"
              size="small"
              color="grey"
              onClick={() => {
                if (state.hub) {
                  rightPanelStore.showPopupVoting('create', state.hub.id)
                }
              }}
              content={Lang.msg.voting.inputButtonHint}
            />
          )}

          {showScreencast && (
            <TwScreencast
              icon="IconVideo"
              size="small"
              color="grey"
              onClick={inputStore.startScreencast}
              content={Lang.msg.screencast.buttonHint}
            />
          )}

          <TwSend
            icon="IconSend"
            size="small"
            color="grey"
            onClick={sendMessage}
            className={
              canSend
                ? cn(
                    '!text-action-default',
                    'hover:!text-action-hover',
                    'active:!text-action-pressed'
                  )
                : cn('!text-grey', 'pointer-events-none')
            }
          />
        </TwActionsContainer>
      </TwMessageInput>
      <Attachments
        files={inputState.chosenFiles}
        messageFiles={inputState.messageFiles}
        onRemove={inputStore.removeFile}
        onRemoveMessageFile={inputStore.removeMessageFile}
        className="my-4 ml-40 mr-16 pr-16 max-h-80 overflow-y-auto"
      />
      {showSubscriberChooser && (
        <ChatSubscribersChooser
          chat={state.hub}
          canEditSubscribers={canEditSubscribers}
        />
      )}
      {showGuestNameChange && <ShareNameChanger />}
    </TwChatInput>
  )
})
